import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { createExperimentalZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import {
    PortfolioCentralCancellationPoliciesUpdateParameters,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { useSuspenseCentralCancellationPolicyGet } from '../../../queries';
import { FormValues, validationSchema } from '../../../schema';
import { CancellationPolicyFormContent } from '../CancellationPolicyFormContent';
import { WithCancellationPolicyId } from '../../../../../cancellationPolicies';
import { getInitialValues } from './initialValues';

export const useEditCancellationPolicyDialogForm = ({ cancellationPolicyId }: WithCancellationPolicyId) =>
    useDialogForm({
        title: lt('CancellationPolicyUpdateFormTitle'),
        renderForm: (props) => <EditCancellationPolicyForm cancellationPolicyId={cancellationPolicyId} {...props} />,
    });

export const EditCancellationPolicyForm = ({
    cancellationPolicyId,
    ...other
}: CommanderFormProps & WithCancellationPolicyId) => {
    const { data } = useSuspenseCentralCancellationPolicyGet(cancellationPolicyId);

    const { mutateAsync } = useRepositoryMutation('portfolioCentralCancellationPolicies', 'update', {
        invalidateQueries: {
            portfolioCentralCancellationPolicies: ['get', 'getOverviewEntries', 'getAll'],
            portfolioCentralRateGroups: ['getAll', 'getOverview'],
        },
        onSuccess: () =>
            showSuccessAlert({
                title: lt('CancellationPolicyUpdateSuccessMessage'),
            }),
    });

    const { renderFormContent } = useHandledDialogFormControls<
        FormValues,
        Omit<PortfolioCentralCancellationPoliciesUpdateParameters, 'centralCancellationPolicyId'>
    >({
        defaultValues: getInitialValues(data),
        id: `update-cancellation-policy-${cancellationPolicyId}`,
        onSubmit: async (values) => mutateAsync({ ...values, centralCancellationPolicyId: cancellationPolicyId }),
        resolver: createExperimentalZodFormResolver(validationSchema),
        ...other,
    });

    return renderFormContent(<CancellationPolicyFormContent shouldDisableBookablePeriodField />);
};
